import React from 'react';
import PropTypes from 'prop-types';
import './InlineNotification.scss';
import Icon from '../Icon/Icon';
import Link from '../Link/Link';
import { createClassList } from '../../utils/dom/class';

/**
 For further guidance on the InlineNotification component, please visit the
   <a target="_blank" href="http://hqcontent.edwardjones.com/GUI/guisite/axiom-ds/components/messaging/inline-notification" class="axiom-link"> AXIOM</a>
    documentation site.
 <br/>
 <br/>
 The presence of the 'onClose' prop will determine whether or not to hide/show the close icon."
*/

const InlineNotification = ({
  text,
  type,
  onClose,
  link,
  customAriaLabel,
  classes,
}) => {
  let iconName;
  switch (type) {
    case 'alert':
      iconName = 'notice-warning';
      break;
    case 'confirmation':
      iconName = 'checkmark-circle';
      break;
    case 'error':
      iconName = 'notice-error';
      break;
    default:
      iconName = 'notice-info';
      break;
  }

  const notificationAriaLabel =
    customAriaLabel || `${type} notification`;

  return (
    <div
      aria-label={notificationAriaLabel}
      role='alert'
      className={createClassList({
        default: [
          'axiom-inline-notification',
          'inline-notification',
          `inline-notification--type--${type}`,
        ],
        custom: classes,
        required: [],
      })}
    >
      <Icon name={iconName} />
      <div className='axiom-inline-notification--text inline-notification-text'>
        {text}
        {link && (
          <span className='axiom-inline-notification--link inline-notification--link'>
            <Link href={link.linkUrl} newWindow>
              {link.linkText}
            </Link>
          </span>
        )}
      </div>
      {onClose && (
        <button
          type='button'
          data-testid='inline-notification-close-button'
          aria-label='dismiss notification'
          className='axiom-inline-notification--button--close close-button'
          onClick={onClose}
        >
          <Icon name='close' />
        </button>
      )}
    </div>
  );
};

InlineNotification.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    .isRequired,
  type: PropTypes.oneOf([
    'informational',
    'alert',
    'confirmation',
    'error',
  ]),
  onClose: PropTypes.func,
  link: PropTypes.shape({
    linkText: PropTypes.string.isRequired,
    linkUrl: PropTypes.string.isRequired,
  }),
  customAriaLabel: PropTypes.string,
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

InlineNotification.defaultProps = {
  type: 'informational',
  onClose: null,
  link: undefined,
  customAriaLabel: undefined,
  classes: [],
};

export default InlineNotification;
